import { Users, Eye, RotateCw, UserMinus, CalendarClock, GitBranch, TrendingDown, BarChart3 } from "lucide-react";

// "Los Dolores del Empresario" — página pilar /dolores-del-empresario
//
// Cada dolor enlaza a 1-2 artículos del blog "Rompiendo el techo" (o a una página del sitio)
// que ya profundizan en el tema, para construir un cluster temático fuerte para SEO.
// Los primeros 6 son los mismos de la Home (PainPoints.jsx); los 2 últimos son exclusivos
// de esta página pilar (utilidad e indicadores), para cubrir el 100% de los 10 artículos.

export const DOLORES = [
  {
    id: "depende-de-ti",
    icon: Users,
    title: "Todo sigue dependiendo de ti",
    bajada: "Cada decisión importante, cada cliente clave y cada problema difícil terminan en tu escritorio.",
    causaRaiz:
      "Esto no pasa porque no confíes en tu equipo. Pasa porque nadie tiene autoridad real ni claridad sobre qué debe decidir sin ti. Mientras la estructura no lo defina, la empresa seguirá dependiendo de tu disponibilidad.",
    solucionEOS:
      "Con un Diagrama de Rendición de Cuentas claro y un ritmo de Rocas trimestrales, tu equipo gana autoridad real para decidir — y tú dejas de ser el cuello de botella.",
    primaryLink: { label: "Cómo dejar de ser el cuello de botella", to: "/empresa-dependiente-del-fundador" },
    secondaryLink: { label: "Responsabilidad sin microgestión", to: "/responsabilidad-sin-microgestion" },
  },
  {
    id: "vision-no-compartida",
    icon: Eye,
    title: "Nadie ve lo que tú ves",
    bajada: "La visión está clara en tu cabeza, pero cada líder ejecuta su propia versión de lo que cree importante.",
    causaRaiz:
      "Una visión que solo vive en la mente del fundador no puede alinear a nadie. Sin un documento compartido y discutido en equipo, cada área interpreta las prioridades a su manera.",
    solucionEOS:
      "El Documento de Visión/Tracción pone la estrategia en una sola página, acordada por todo el equipo de liderazgo — para que todos remen en la misma dirección.",
    primaryLink: { label: "Conoce el modelo completo de EOS", to: "/el-modelo-eos" },
    secondaryLink: { label: "Cuando el equipo evita las conversaciones difíciles", to: "/equipo-liderazgo-conversaciones-dificiles" },
  },
  {
    id: "problemas-se-repiten",
    icon: RotateCw,
    title: "Los problemas se repiten",
    bajada: "Apagas el mismo incendio una y otra vez, solo que con otro nombre.",
    causaRaiz:
      "Resolver síntomas sin llegar a la causa raíz garantiza que el problema regrese. Sin un método común para discutirlo, cada solución es un parche, no una cura.",
    solucionEOS:
      "El método IDS (Identificar, Discutir, Solucionar) y una Lista de Asuntos visible permiten resolver los problemas de raíz — de una vez, no cada semana.",
    primaryLink: { label: "Cómo dejar de apagar incendios", to: "/dejar-de-apagar-incendios-empresa" },
  },
  {
    id: "persona-equivocada",
    icon: UserMinus,
    title: "Sabes quién no debería estar en el equipo",
    bajada: "Lo has pensado mil veces, pero la conversación se sigue postergando.",
    causaRaiz:
      "Sin criterios objetivos, evaluar a una persona se vuelve una decisión emocional y postergable. El costo de no actuar lo paga todo el equipo.",
    solucionEOS:
      "El Analizador de Personas te da un lenguaje común y objetivo para saber si tienes a la Persona Correcta en el Puesto Correcto — y actuar con la razón, no solo con la emoción.",
    primaryLink: { label: "¿Tienes a la persona correcta en el puesto correcto?", to: "/persona-correcta-puesto-correcto" },
  },
  {
    id: "reuniones-sin-ejecucion",
    icon: CalendarClock,
    title: "Muchas reuniones, poca ejecución",
    bajada: "Se conversa mucho, se acuerda poco y los compromisos rara vez se cumplen.",
    causaRaiz:
      "Las reuniones sin agenda fija se convierten en actualizaciones informativas, no en espacios de decisión. El tiempo se va en informar, no en resolver.",
    solucionEOS:
      "La Reunión Nivel 10 — 90 minutos, misma agenda, cada semana — transforma las reuniones en el motor de ejecución de la empresa, no en una pérdida de tiempo.",
    primaryLink: { label: "Reuniones largas, decisiones débiles", to: "/reuniones-largas-sin-resultados" },
  },
  {
    id: "complejidad-creciente",
    icon: GitBranch,
    title: "La complejidad crece más rápido que el negocio",
    bajada: "Más personas, más procesos, más áreas… y menos foco.",
    causaRaiz:
      "Sin una estructura diseñada para el tamaño que quieres alcanzar, cada nueva contratación agrega complejidad en lugar de capacidad.",
    solucionEOS:
      "El Diagrama de Rendición de Cuentas se diseña mirando los próximos 6 a 12 meses, no para hoy — así la estructura crece con intención, no por improvisación.",
    primaryLink: { label: "Roles confusos: cuando nadie responde", to: "/roles-responsabilidades-confusas-empresa" },
    secondaryLink: { label: "Cuando todo escala al dueño", to: "/problemas-escalan-al-dueno" },
  },
  {
    id: "vendes-mas-ganas-menos",
    icon: TrendingDown,
    title: "Vendes más, pero ganas menos",
    bajada: "Las ventas suben, pero la utilidad no acompaña — y no sabes exactamente dónde se está perdiendo.",
    causaRaiz:
      "El estado de resultados mensual explica lo que ya pasó, no lo que está pasando. Sin números semanales que anticipen el margen, la rentabilidad se gestiona tarde.",
    solucionEOS:
      "Un Tablero de Indicadores semanal, con responsables claros por cada número, permite detectar la fuga de utilidad antes del cierre de mes — no después.",
    primaryLink: { label: "¿Dónde se está perdiendo la utilidad?", to: "/ventas-crecen-utilidad-baja" },
  },
  {
    id: "sin-visibilidad-semanal",
    icon: BarChart3,
    title: "No sabes cómo va tu negocio hasta fin de mes",
    bajada: "Conduces mirando el espejo retrovisor: cuando ves el resultado, ya es tarde para corregirlo.",
    causaRaiz:
      "Sin un pulso semanal del negocio, cada decisión se basa en percepción, no en datos. Los problemas se descubren cuando ya crecieron.",
    solucionEOS:
      "Entre 5 y 15 números semanales, con meta y responsable, te dan visibilidad real del negocio — y tiempo para actuar antes de que sea tarde.",
    primaryLink: { label: "Cómo anticipar si el negocio está fuera de rumbo", to: "/indicadores-semanales-negocio" },
  },
];
